// Estilos da lista/bolhas de mensagens (M5.3a) da modernização do InfoChat.
//
// Função pura — sem JSX, sem I/O, sem leitura de localStorage/backend.
// Recebe o `theme` do MUI (idealmente já com `theme.infoChat` da Entrega
// M2) e devolve só objetos de estilo simples (CSS-in-JS), para serem
// mesclados nas classes já existentes de `MessagesList`. Não aplica nada
// sozinha — quem decide onde/como usar é esse arquivo.
//
// Helper independente das demais entregas (M5.1/M5.2) — por decisão
// explícita, não importa nem se acopla a elas, mesmo definindo um
// `focusRingAccent` equivalente (duplicação intencional, cada entrega fica
// isolada).
//
// Escopo desta entrega (M5.3a): só separador de data, bolha central de
// chamada perdida, texto de mensagem apagada e o botão de opções da
// mensagem (cor/hover/foco). Nenhuma cor de bolha (recebida/enviada/
// privada), de citação, de estado de entrega/leitura (ack) ou de fila é
// decidida aqui — permanecem exatamente como estão em `MessagesList`, por
// decisão explícita de preservação de identidade visual.
//
// Usa exclusivamente os tokens de `theme.infoChat` para cor — nunca um hex
// literal novo aqui.

import { createInfoChatDesignTokens } from "../theme/infoChatDesignTokens";

const hasCompleteInfoChatTokens = tokens =>
  Boolean(
    tokens &&
      tokens.brand &&
      tokens.surfaces &&
      tokens.borders &&
      tokens.text &&
      tokens.shadows &&
      tokens.transitions &&
      tokens.radii &&
      tokens.spacing &&
      tokens.status
  );

// Mesmo padrão de fallback seguro já usado nas entregas anteriores — nunca
// deixa a lista de mensagens quebrar caso theme.infoChat esteja
// ausente/incompleto, e nunca diverge dos tokens reais por reaproveitar a
// mesma função pura da M2.
const resolveInfoChatTokens = theme => {
  if (hasCompleteInfoChatTokens(theme?.infoChat)) {
    return theme.infoChat;
  }
  return createInfoChatDesignTokens(theme?.palette?.type, theme?.palette?.primary?.main);
};

/**
 * Estilos da lista/bolhas de mensagens modernizados (M5.3a).
 *
 * @param {object} theme - tema do MUI (idealmente já com `theme.infoChat`).
 */
export const createInfoChatMessagesListStyles = theme => {
  const tokens = resolveInfoChatTokens(theme);

  return {
    // Separador de data ("HOJE", "dd/MM/yyyy") — antes "#e1f3fb"/"#808888"
    // fixos, sem reagir ao modo escuro.
    dailySeparatorSurface: {
      backgroundColor: tokens.surfaces.subtle
    },
    dailySeparatorText: {
      color: tokens.text.secondary
    },

    // Bolha central de chamada perdida — antes "#E1F5FEEB" fixo, sem
    // reagir ao modo escuro.
    callLogSurface: {
      backgroundColor: tokens.surfaces.subtle
    },
    // Texto da mesma bolha — antes "#272727" fixo (cinza quase-preto),
    // sem contraste garantido contra `callLogSurface` no modo escuro.
    callLogText: {
      color: tokens.text.primary
    },

    // Texto de mensagem apagada (corpo, itálico) — antes
    // "rgba(0, 0, 0, 0.36)" fixo, quase ilegível no modo escuro por nunca
    // reagir a ele. Não é o aviso "🚫 Essa mensagem foi apagada..." (esse
    // usa a cor de estado já existente `deletedMessage`, fora do escopo
    // desta migração) — é só o corpo esmaecido do texto original.
    deletedTextColor: {
      color: tokens.text.secondary
    },

    // Cor do botão de opções da mensagem ("...") — antes "#999" fixo, sem
    // token.
    actionIconColor: {
      color: tokens.text.secondary
    },
    // Hover do mesmo botão — antes `backgroundColor: "inherit"` (sem
    // nenhum indicativo visual de hover). Tom institucional sutil, mesmo
    // padrão de alpha hex já usado nas entregas anteriores.
    actionIconHoverSurface: {
      backgroundColor: `${tokens.brand.primaryColor}0D`
    },

    // Anel de foco visível (navegação real por teclado) — mesma cor
    // institucional, mesmo raciocínio já corrigido em M4/M5.1/M5.2: usa
    // `outline`/`outlineOffset`, nunca `boxShadow` ou `backgroundColor`,
    // para nunca disputar a mesma propriedade usada pelo hover acima — o
    // foco sempre aparece por cima de qualquer estado, sem sobrescrever
    // nada.
    focusRingAccent: {
      outline: `2px solid ${tokens.brand.primaryColor}`,
      outlineOffset: "-2px"
    }
  };
};

export default createInfoChatMessagesListStyles;
